import { ElementType, PropsWithChildren, ReactNode } from 'react';

import { Link } from '@mui/material';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';

import {
  Icons,
  mergeSx,
  SxProps,
  InfoTooltip,
  InfoTooltipProps,
  mapPaletteColorToDataColorToken,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { Marker, MarkerProps } from 'components/dividers';

import type { TypographyProps, StackProps } from '@cast/design-system';

type LinkWrapperProps = { link?: string; iconSize?: number };
export type MetricsBoxProps<C extends ElementType = any> = {
  markerColor?: string;
  title: ReactNode;
  values?: ReactNode;
  primaryValue: ReactNode;
  secondaryValue?: ReactNode;
  titleProps?: TypographyProps<C>;
  valuesContainerProps?: StackProps;
  valueStackProps?: StackProps;
  linkWrapperProps?: LinkWrapperProps;
  primaryValueProps?: TypographyProps<C>;
  secondaryValueProps?: TypographyProps<C> & { suffix?: ReactNode };
  markerProps?: Partial<MarkerProps>;
  gap?: number;
  valueLink?: string;
  tooltip?: ReactNode;
  tooltipProps?: Partial<InfoTooltipProps>;
  sx?: SxProps;
  className?: string;
  testId?: string;
  extraBottomContent?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

const LinkWrapper = ({
  link,
  iconSize = 14,
  children,
}: PropsWithChildren<LinkWrapperProps>) => {
  if (!link) {
    return <>{children}</>;
  }
  return (
    <Link
      to={link}
      component={RouterLink}
      sx={{
        textDecoration: 'none !important',
        display: 'inline-flex',
        gap: 5,
        alignItems: 'baseline',
        width: 'max-content',
        '& > svg': {
          visibility: 'hidden',
        },
        '&:hover': {
          '& .MetricsBox-value, & .MetricsBox-secondaryValue': {
            color: 'inherit',
          },
          '& svg': {
            visibility: 'initial',
          },
        },
      }}
    >
      {children}
      <Icons.ArrowSquareOut size={iconSize} color="currentColor" />
    </Link>
  );
};

export const Values = <C extends ElementType = any>({
  primaryValue,
  primaryValueProps,
  secondaryValue,
  secondaryValueProps,
  gap = 4,
  testId,
  stackProps,
}: Pick<
  MetricsBoxProps<C>,
  | 'primaryValue'
  | 'primaryValueProps'
  | 'secondaryValue'
  | 'secondaryValueProps'
  | 'gap'
  | 'testId'
> & { stackProps: StackProps }) => (
  <Stack
    direction="row"
    gap={gap}
    alignItems="baseline"
    {...stackProps}
    position="relative"
  >
    <Typography
      className="MetricsBox-value"
      noWrap
      data-testid={`${testId}-primary-value`}
      {...(!primaryValueProps?.variant && {
        // Only override line height if a custom variant isn't provided
        variant: 'h4',
        lineHeight: '28px',
      })}
      {...primaryValueProps}
    >
      {primaryValue}
    </Typography>
    {!!secondaryValue && (
      <Typography
        component="span"
        {...secondaryValueProps}
        className={clsx(
          'MetricsBox-secondaryValue',
          secondaryValueProps?.className
        )}
        data-testid={`${testId}-secondary-value`}
      >
        {secondaryValue}
        {!!secondaryValueProps?.suffix && (
          <Typography
            component="span"
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
          >
            {' '}
            {secondaryValueProps?.suffix}
          </Typography>
        )}
      </Typography>
    )}
  </Stack>
);

export const MetricsBox = <C extends ElementType = 'span'>({
  markerColor,
  title,
  primaryValue,
  secondaryValue,
  titleProps,
  linkWrapperProps = {},
  valuesContainerProps = {},
  valueStackProps = {},
  primaryValueProps,
  secondaryValueProps,
  markerProps,
  tooltip,
  tooltipProps,
  gap = 8,
  valueLink,
  sx,
  className,
  extraBottomContent,
  disabled,
  onClick,
  testId = 'metrics-box',
}: MetricsBoxProps<C>) => {
  return (
    <Box
      className={clsx('MetricsBox-root', className)}
      display="flex"
      alignItems="center"
      sx={mergeSx(
        onClick && {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: tokens.theme.bg.action.surfaceHover,
          },
        },
        sx
      )}
      data-testid={testId}
      onClick={onClick}
    >
      <Stack
        minWidth={0}
        width="100%"
        position="relative"
        justifyContent="center"
        paddingLeft={markerColor ? 12 : 0}
        {...valuesContainerProps}
        sx={{ opacity: disabled ? 0.25 : 1 }}
      >
        {!!markerColor && (
          <Marker
            height="100%"
            width={2}
            flexShrink={0}
            {...markerProps}
            color={mapPaletteColorToDataColorToken(markerColor, markerColor)}
            sx={{
              opacity: disabled ? 0.25 : 1,
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
            }}
          />
        )}

        <Typography
          variant="L12R"
          component="div"
          color={tokens.theme.fg.structural.primary}
          noWrap
          data-testid={`${testId}-title`}
          display="inline-flex"
          alignItems="center"
          gap="4px"
          {...titleProps}
          className={clsx('MetricsBox-title', titleProps?.className)}
        >
          {title}
          {tooltip && (
            <InfoTooltip
              data-testid={`${testId}-info-tooltip`}
              title={tooltip}
              placement="top"
              {...tooltipProps}
              iconSx={mergeSx({ flexShrink: 0 }, tooltipProps?.iconSx)}
            />
          )}
        </Typography>

        <LinkWrapper link={valueLink} {...linkWrapperProps}>
          <Values
            primaryValue={primaryValue}
            primaryValueProps={primaryValueProps}
            secondaryValue={secondaryValue}
            secondaryValueProps={secondaryValueProps}
            gap={gap}
            testId={testId}
            stackProps={valueStackProps}
          />
        </LinkWrapper>
        {extraBottomContent && (
          <Box data-testid={`${testId}-extra-bottom-content`}>
            {extraBottomContent}
          </Box>
        )}
      </Stack>
    </Box>
  );
};
